/* eslint-disable class-methods-use-this */
/* eslint-disable no-return-assign */
import { html, css, LitElement, TemplateResult } from 'lit';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';
import '@ui5/webcomponents/dist/Icon';
import '@ui5/webcomponents/dist/Button';
import '@ui5/webcomponents/dist/SegmentedButton';
import '@ui5/webcomponents/dist/SegmentedButtonItem';
import { customElement, property } from 'lit/decorators.js';
import '../ki-icon/ki-icon-btn';
import { getConfig } from '@kisters/wiski-web/components';
import first from 'lodash-es/first';
import get from 'lodash-es/get';
import { navigateTo } from '../ki-app/routing/ki-router';
import { i18nMixin, responsiveMixin } from '../../decorators';
import { Mix } from '../../common';
import { isActivationKey } from '../ki-app';
import nls from '../../locales/index';
import { WiskiStation } from '../../defs/WiskiTypes';
import { SortedBy } from '../wwp-overview/SortedBy';

@customElement('ww-simple-dashboard')
export default class WwSimpleDashboard extends Mix(
  LitElement,
  responsiveMixin,
  [i18nMixin, { nls }],
) {
  static styles = css`
    :host {
      --font-size-base: 1rem;
      --font-size-large: 1.5rem;
      display: flex;
      flex-direction: column;
      height: 100%;
      align-items: center;
    }

    .dashboard-container {
      height: 100%;
      min-height: 250px;
      padding: 0px;
      display: flex;
      z-index: 1;
    }

    .graph-container {
      height: 100%;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-evenly;
      align-content: center;
      gap: 0px;
      position: relative;
    }

    .dashboard-popup {
      width: 400px;
      height: 300px;
    }

    .pagination {
      display: flex;
      align-self: center;
      align-items: center;
      width: fit-content;
      padding: 0.3rem 0.3rem;
      margin-top: 0.4rem;
    }

    .count:hover {
      font-size: 1.1rem;
    }

    .count.selected {
      font-weight: bold;
      font-size: 1.1rem;
      border: solid 0.1rem;
    }

    .pagination-mobile-btn {
      display: none;
    }
    .prev-btn,
    .next-btn {
      align-self: center;
      cursor: pointer;
      z-index: 1000;
    }

    .prev-btn {
      margin-left: 10px;
    }
    .next-btn {
      margin-right: 10px;
    }

    .pagination-btn {
      width: 4rem;
      height: 4rem;
    }

    .pagination-icon {
      width: 3rem;
      height: 3rem;
    }

    .pagination-mobile {
      display: none;
    }

    /* a-Tag should not appear as a link/modify layout */
    .stationLink {
      text-decoration: inherit;
      color: inherit;
      font: inherit;
      background: inherit;
      border: inherit;
      margin: 3px;
      border-radius: 5px;
    }

    .stationLink:hover,
    .stationLink:focus {
      background: #eee; /* Falls der Link einen Hintergrund haben könnte */
      border: 3px solid #eee;
      margin: 0px;
    }

    @media only screen and (max-width: 800px) {
      .graph-container {
        display: flex;
      }
      .graph-container > * {
        width: 100%;
      }

      .prev-btn,
      .next-btn {
        display: none;
      }

      .pagination-mobile {
        display: flex;
        flex-wrap: wrap;
        align-self: center;
        z-index: 1000;
        margin-bottom: 0.5rem;
      }

      .pagination {
        display: none;
      }
    }
  `;

  @property({ type: Array, attribute: false })
  stations = [];

  @property({ type: String, attribute: false })
  sort = '[{"field":"station_name", "ascending":true}]';

  @property({ type: String, attribute: false })
  popup = '';

  @property({ type: String, attribute: false })
  layerAlias = '';

  @property({ type: String, attribute: false })
  detailsPath = '';

  @property({ type: Number })
  selectedStep = 1;

  @property({ type: Number })
  totalSteps = 0;

  @property({ type: Boolean })
  isMobile = false;

  @property({ type: Boolean })
  shouldHideScreenArrows = false;

  @property({ type: Boolean })
  shouldHidePaginationArrows = true;

  @property({ type: Number })
  graphsPerPage = 0;

  popupX = 400;

  popupY = 300;

  firstUpdated() {
    this.updateTotalSteps();
    this.checkMaxWidth();
    this._sortData();
  }

  constructor() {
    super();
    const config = getConfig()?.pagination;
    this.shouldHidePaginationArrows = config?.shouldHidePaginationArrows;
    this.shouldHideScreenArrows = config?.shouldHideScreenArrows;
  }

  checkMaxWidth() {
    const handleMaxWidthChange = () => {
      this.isMobile = false;
      this.selectedStep = 1;

      const mediaQueryMobile = window.matchMedia('(max-width: 600px)');
      const mediaQueryMaxHeight = window.matchMedia('(max-height: 515px)');

      this.graphsPerPage =
        this.stationsGridXCount() * this.stationsGridYCount();

      if (mediaQueryMobile.matches) {
        this.isMobile = true;
        this.graphsPerPage = 1;
      } else {
        this.isMobile = false;
      }

      if (mediaQueryMaxHeight.matches) {
        this.graphsPerPage = 0;
      }
      this.updateTotalSteps();
    };

    window.addEventListener('resize', handleMaxWidthChange);
    handleMaxWidthChange(); // Call initially
  }

  /** Num of station in X-dimension (left -> right) */
  stationsGridXCount(): number {
    const bigBtns = this.shadowRoot.querySelectorAll('.big-btn');
    let offsetBtn = 20; // margin sum of both buttons
    Array.from(bigBtns).forEach(btn => {
      const btnsWidth = btn as HTMLElement;
      offsetBtn += btnsWidth.clientWidth + btnsWidth.offsetWidth;
    });
    const xCount = Math.floor((this.clientWidth - offsetBtn) / this.popupX);
    return xCount || 0;
  }

  /** Num of station in Y-dimension (top -> bottom) */
  stationsGridYCount(): number {
    const offsetPagination =
      this.shadowRoot?.querySelector('.pagination')?.offsetHeight ?? 0;
    const yCount = Math.floor(
      (this.clientHeight - offsetPagination) / this.popupY,
    );
    return yCount || 0;
  }

  updateTotalSteps() {
    this.graphsPerPage > 0
      ? (this.totalSteps = Math.ceil(this.stations.length / this.graphsPerPage))
      : (this.totalSteps = Math.ceil(this.stations.length / 1));
  }

  update(changedProperties: Map<string, any>) {
    if (
      changedProperties.has('stations') &&
      changedProperties.get('stations')
    ) {
      this.selectedStep = 1;
       this._sortData();
      this.updateTotalSteps();
    }
    if (
      changedProperties.has('sort') &&
      changedProperties.get('sort')
    ) {
      this._sortData();
    }
    super.update();
  }

  _getNavItems() {
    const steps: TemplateResult[] = [];
    const totalPages = Math.ceil(this.stations.length / this.graphsPerPage);

    if (totalPages <= 8) {
      // If there are 8 or fewer pages, simply display all pages
      for (let i = 1; i <= totalPages; i += 1) {
        steps.push(this._createNavItem(i));
      }
    } else {
      const { showFirst, showLast, showMiddle } = this._calculateShowCounts();

      for (let i = 1; i <= showFirst; i += 1) {
        steps.push(this._createNavItem(i));
      }

      if (this.selectedStep > showFirst + 1) {
        steps.push(this._createEllipsisItem());
      }

      const start = Math.max(showFirst + 1, this.selectedStep - showMiddle);
      const end = Math.min(
        totalPages - showLast,
        this.selectedStep + showMiddle,
      );

      for (let i = start; i <= end; i += 1) {
        steps.push(this._createNavItem(i));
      }

      if (this.selectedStep < totalPages - showLast) {
        steps.push(this._createEllipsisItem());
      }

      for (let i = totalPages - showLast + 1; i <= totalPages; i += 1) {
        steps.push(this._createNavItem(i));
      }
    }

    return steps;
  }

  _createNavItem(index) {
    return html`
      <ui5-segmented-button-item
        tabindex="0"
        @click="${() => {
          this.selectedStep = index;
        }}"
        @keydown="${e => {
          if (isActivationKey(e)) this.selectedStep = index;
        }}"
        class="count ${this.selectedStep === index ? 'selected' : ''}"
      >
        ${index}
      </ui5-segmented-button-item>
    `;
  }

  _createEllipsisItem() {
    return html`
      <ui5-segmented-button-item disabled>...</ui5-segmented-button-item>
    `;
  }

  _calculateShowCounts() {
    const showFirst = 2;
    const showLast = 2;
    const showMiddle = 2;
    return { showFirst, showLast, showMiddle };
  }

  _getPagination() {
    if (this.totalSteps) {
      if (this.shouldHidePaginationArrows && !this.isMobile) {
        return html`
          <ui5-segmented-button> ${this._getNavItems()} </ui5-segmented-button>
        `;
      }
      return html`
        <ui5-segmented-button>
          <ui5-segmented-button-item
            @click="${this.handlePrevPage}"
            @keydown="${e => {
              if (isActivationKey(e)) this.handlePrevPage();
            }}"
          >
            <ui5-icon name="navigation-left-arrow"></ui5-icon>
          </ui5-segmented-button-item>

          ${this._getNavItems()}

          <ui5-segmented-button-item
            @click="${this.handleNextPage}"
            @keydown="${e => {
              if (isActivationKey(e)) this.handleNextPage();
            }}"
          >
            <ui5-icon name="navigation-right-arrow"></ui5-icon>
          </ui5-segmented-button-item>
        </ui5-segmented-button>
      `;
    }
    return html``;
  }

  handlePrevPage() {
    if (this.selectedStep > 1) {
      this.selectedStep -= 1;
      this.requestUpdate();
    }
  }

  handleNextPage() {
    if (this.selectedStep < this.totalSteps) {
      this.selectedStep += 1;
      this.requestUpdate();
    }
  }

  navigateToStation(station: WiskiStation) {
    // Extract search parameters from the current URL
    const searchParams = new URLSearchParams(
      window.location.hash.split('?')[1],
    );

    // Extract mode and period if present
    const mode = searchParams.get('mode')
      ? `?mode=${searchParams.get('mode')}`
      : '';
    const period = searchParams.get('period')
      ? `&period=${searchParams.get('period')}`
      : '';

    // Construct new path for station navigation
    const lPath = this.detailsPath ?? this.layerAlias
    const stationPath = `/overview/${lPath}/station/${station.station_id}/${encodeURIComponent(station.station_name)}/${this.layerAlias}${mode}${period}`;

    // Perform navigation
    navigateTo(stationPath);
  }

  render() {
    if (this.graphsPerPage > 0) {
      const popupTagName = unsafeStatic(this.popup);
      return html`
        <div class="pagination">${this._getPagination()}</div>

        <div class="dashboard-container" id="dashboard-container">
          ${!this.shouldHideScreenArrows
            ? html`
                <div class="prev-btn big-btn">
                  <ui5-segmented-button-item
                    class="pagination-btn"
                    @click="${this.handlePrevPage}"
                    @keydown="${e => {
                      if (isActivationKey(e)) this.handlePrevPage();
                    }}"
                  >
                    <ui5-icon
                      class="pagination-icon"
                      name="navigation-left-arrow"
                    ></ui5-icon>
                  </ui5-segmented-button-item>
                </div>
              `
            : ''}
          <div class="graph-container">
            ${this.stations
              .slice(
                this.selectedStep * this.graphsPerPage - this.graphsPerPage,
                this.selectedStep * this.graphsPerPage,
              )
              .map(
                station =>
                  staticHtml`<div class="stationLink" aria-label="${this.i18n.t('ARIA.miniGraphLink')} ${station.station_name} (${station.WTO_OBJECT})" @click="${() => {
                    this.navigateToStation(station);
                  }}" @keydown="${e => {
                    if (isActivationKey(e)) this.navigateToStation(station);
                  }}" tabindex="0"><${popupTagName} class="dashboard-popup" flexSize .layerAlias="${
                    this.layerAlias
                  }" .stations="${[station]}"></${popupTagName}></div>`,
              )}
          </div>
          ${!this.shouldHideScreenArrows
            ? html`
                <div class="next-btn big-btn">
                  <ui5-segmented-button-item
                    class="pagination-btn"
                    @click="${this.handleNextPage}"
                    @keydown="${e => {
                      if (isActivationKey(e)) this.handleNextPage();
                    }}"
                  >
                    <ui5-icon
                      class="pagination-icon"
                      name="navigation-right-arrow"
                    ></ui5-icon>
                  </ui5-segmented-button-item>
                </div>
              `
            : ''}
        </div>

        <div class="pagination-mobile">${this._getPagination()}</div>
      `;
    }
    return html``;
  }

  _sortData() {
    if(typeof this.sort === 'string') {
      this.sort = JSON.parse(this.sort);
    }
    // Check if we should apply DIN5007 / defined in layercfg -> col
    if (first(this.sort)?.useDIN5007) this.normalizeSpecialCharacters = true;

    
    if (this.sort.length > 0) {
      if (!this.multisort) {
        this.sort = this.sort.slice(0, 1);
      }

      this.stations.sort((a, b) => {
        const sort = [...this.sort];
        let result = 0;
        do {
          const _sort = sort.shift();
          const compareProperty = _sort.sortBy || _sort.field;
          let aValue = get(a, compareProperty);
          let bValue = get(b, compareProperty);
          if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            if (this.normalizeSpecialCharacters)
              aValue = this._normalizeSpecChars(aValue);
          }
          if (typeof bValue === 'string') {
            bValue = bValue.toLowerCase();
            if (this.normalizeSpecialCharacters)
              bValue = this._normalizeSpecChars(bValue);
          }
          const format =
            typeof _sort.format === 'string'
              ? _sort.format
              : _sort.format?.type;
          if (['textnumber', 'number', 'wiskinumber'].indexOf(format) >= 0) {
            const parsedA =
              typeof aValue === 'string'
                ? parseFloat(aValue.replace(',', ''))
                : aValue;
            const parsedB =
              typeof bValue === 'string'
                ? parseFloat(bValue.replace(',', ''))
                : bValue;
            if (Number.isNaN(parsedA) && Number.isNaN(parsedB)) {
              aValue = 0;
              bValue = 0;
            } else if (Number.isNaN(parsedA)) {
              bValue = parsedB;
              aValue = bValue - 1;
            } else if (Number.isNaN(parsedB)) {
              aValue = parsedA;
              bValue = aValue - 1;
            } else {
              aValue = parsedA;
              bValue = parsedB;
            }
          }
          // Handle null/undefined values
          if (aValue == null && bValue == null) {
            result = 0;
          } else if (aValue == null) {
            result = -1 * (_sort.ascending ? 1 : -1);
          } else if (bValue == null) {
            result = 1 * (_sort.ascending ? 1 : -1);
          } else if (aValue === bValue) {
            result = 0;
          } else {
            result = (aValue > bValue ? 1 : -1) * (_sort.ascending ? 1 : -1);
          }
        } while (result === 0 && sort.length > 0);
        return result;
      });
    }
  }

  // Enforce DIN5007 sorting order
  _normalizeSpecChars(str: string): string {
    let _s: string = str;
    _s = _s.replaceAll('ä', 'a');
    _s = _s.replaceAll('ö', 'o');
    _s = _s.replaceAll('ü', 'u');
    _s = _s.replaceAll('Ä', 'A');
    _s = _s.replaceAll('Ö', 'O');
    _s = _s.replaceAll('Ü', 'U');
    _s = _s.replaceAll('ß', 'ss');
    return _s;
  }
}
